import { useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import type { MemberCredential } from "../api/types";

export interface BulkCredentialFailure {
  id: string;
  message: string;
}

function credentialLine(credential: MemberCredential): string {
  return `${credential.id}: ${credential.password}`;
}

function CredentialRow({ id, password }: { id: string; password: string }) {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(`${id}: ${password}`);
      setCopyError(false);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
      setCopyError(true);
    }
  }

  return (
    <Stack spacing={0.25}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Typography variant="body2" sx={{ fontFamily: "monospace", flexGrow: 1, wordBreak: "break-all" }}>
          {id}: {password}
        </Typography>
        <Tooltip title={`Copy credentials for ${id}`}>
          <IconButton aria-label={`Copy credentials for ${id}`} onClick={handleCopy} size="small">
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Typography
        variant="caption"
        color={copyError ? "error.main" : "success.main"}
        aria-live="polite"
        sx={{ minHeight: "1em" }}
      >
        {copyError ? "Could not copy automatically. Select and copy the text manually." : copied ? "Copied." : ""}
      </Typography>
    </Stack>
  );
}

export interface BulkCredentialDialogProps {
  open: boolean;
  title: string;
  /**
   * Every member whose password reset succeeded, in call order. Owned by
   * the caller's own state and set back to an empty array on close (same
   * one-time semantics as OneTimeCredentialDialog): never persisted, never
   * re-fetched or re-shown once cleared.
   */
  credentials: MemberCredential[];
  /** Members whose reset failed, each with the error message to show, so a partial failure is never silently dropped. */
  failures: BulkCredentialFailure[];
  onClose: () => void;
}

/**
 * The bulk equivalent of OneTimeCredentialDialog: one dialog listing every
 * member ID with its new password after a "Reset password" bulk action
 * (StaffSharingSettingsTab), a copy button per row, and a "Copy all" that
 * copies every "ID: password" line at once. Same one-time contract: never
 * dismissible by backdrop click or Escape (only the explicit Done button),
 * and the caller clears `credentials`/`failures` on close.
 */
export function BulkCredentialDialog({ open, title, credentials, failures, onClose }: BulkCredentialDialogProps) {
  const [copiedAll, setCopiedAll] = useState(false);
  const [copyAllError, setCopyAllError] = useState(false);

  // See OneTimeCredentialDialog: intentionally ignores backdropClick and
  // escapeKeyDown, since this dialog holds passwords shown exactly once.
  function ignoreBackdropAndEscape() {
    // Intentionally does nothing.
  }

  async function handleCopyAll() {
    try {
      await navigator.clipboard.writeText(credentials.map(credentialLine).join("\n"));
      setCopyAllError(false);
      setCopiedAll(true);
      window.setTimeout(() => setCopiedAll(false), 1500);
    } catch {
      setCopiedAll(false);
      setCopyAllError(true);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={ignoreBackdropAndEscape}
      maxWidth="sm"
      fullWidth
      aria-labelledby="bulk-credential-dialog-title"
    >
      <DialogTitle id="bulk-credential-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {credentials.length > 0 && (
            <Alert severity="warning">
              These passwords will not be shown again, and each stays valid until the partner
              changes it. Copy the values now, and send them to each partner through two separate
              channels, for example email for the member ID and phone for the password.
            </Alert>
          )}

          {failures.length > 0 && (
            <Alert severity="error">
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {failures.length === 1
                  ? "1 member could not be reset:"
                  : `${failures.length} members could not be reset:`}
              </Typography>
              <Stack spacing={0.25}>
                {failures.map((failure) => (
                  <Typography key={failure.id} variant="body2">
                    {failure.id}: {failure.message}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}

          {credentials.length > 0 && (
            <>
              <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  New passwords ({credentials.length})
                </Typography>
                <Button size="small" startIcon={<ContentCopyIcon fontSize="small" />} onClick={handleCopyAll}>
                  Copy all
                </Button>
              </Stack>
              <Typography
                variant="caption"
                color={copyAllError ? "error.main" : "success.main"}
                aria-live="polite"
                sx={{ minHeight: "1em", mt: -2 }}
              >
                {copyAllError
                  ? "Could not copy automatically. Select and copy the text manually."
                  : copiedAll
                    ? "All credentials copied."
                    : ""}
              </Typography>
              <Stack spacing={1} divider={<Divider flexItem />}>
                {credentials.map((credential) => (
                  <CredentialRow key={credential.id} id={credential.id} password={credential.password} />
                ))}
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" autoFocus>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
